import { FormikControl, useModel } from "@stem-sims/nexus"
import FloatingPage from "components/General/FloatingPage"
import { FormikProvider, Form as FormikForm, useFormik } from "formik"
import { object, string, number } from "yup"
import { Button, ButtonGroup, Container, Form, ToggleButton } from "react-bootstrap"
import { useState } from "react"
import { LandingPageScienceProduct, LandingPageScienceProducts } from "shared/types/science35Types"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPaperPlane, faRocket } from "@fortawesome/free-solid-svg-icons"
import { requestScienceAccount } from "shared/routes/science/requestScienceAccounts"
import { toast } from "react-toastify"
import styles from "./styles.module.scss"
import stateRoutes from "shared/routes/states"
import { BeatLoader } from "react-spinners"

const ScienceLandingPage = () => {
    const [product, setProduct] = useState<LandingPageScienceProduct>("Science 35")
    const [submitted, setSubmitted] = useState(false)

    const { response: statesInfo, loading: loadingStates } = useModel({
          model: stateRoutes.get
      })
 
    const formik = useFormik({
        initialValues: {
            name: "",
            email: "",
            numOfStudents: 25,
            comment: "",
            state: "New York",
        },
        validateOnBlur: false,
        validationSchema: object().shape({
          name: string().required("Name is required."),
          email: string().email().required("Email is required."),
          numOfStudents: number().required("Number of students is required.")
                        .min(1, "You must have at least 1 student.").max(250, "Please contact STEM Sims if you need more than 250 students."),
          comment: string().optional(),
          state: string().required("State is required."),
        }),
        onSubmit: (values, { setSubmitting }) => {
          requestScienceAccount({ ...values, product })
            .then(() => {
              toast.success("Submitted the account request.")
              setSubmitted(true)
            })
            .catch(() => {
              toast.error("Something went wrong.")
            })
            .finally(() => {
              setSubmitting(false)
            })
        }
    })

    const submitDisabled = formik.isSubmitting || !formik.dirty || !formik.isValid

    if (loadingStates) return <BeatLoader />

  return (
    <FloatingPage className={styles.scienceLandingPage}>
        <Container fluid className={`d-flex align-items-center justify-content-center p-0`}>
          <div className="d-md-flex justify-content-around flex-row-reverse">
            <div className="d-flex align-items-center justify-content-center">
              <div>
                <h3 className="fw-bold"><FontAwesomeIcon icon={faRocket} className="me-1 text-primary" />Sign up for Science 35 and/or Science 68</h3>
                <div className="my-3" />
                <FormikProvider value={formik}>
                  <Form as={FormikForm} className="text-center">

                      <Form.Group className="d-inline-block w-100 text-start mb-4">
                        <Form.Label>Product*</Form.Label>
                        <ButtonGroup className={`text-start d-block`}>
                            {LandingPageScienceProducts.map((option) => {
                                return (
                                    <ToggleButton 
                                        id={`${option}`}
                                        type="radio"
                                        name="studentAssessmentViewOption"
                                        className={`${product === option ? "bg-primary" : "bg-info"} rounded border-0 fw-bold me-2 mb-1`}
                                        value={option}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            e.stopPropagation()
                                            setProduct(option)
                                            formik.handleChange(e)
                                        }}>
                                            <span className={`text-white`}>{ option }</span>
                                    </ToggleButton>
                                )
                            })}
                        </ButtonGroup>
                      </Form.Group>

                      <Form.Group className="d-inline-block w-100 text-start mb-4">
                        <Form.Label>Name*</Form.Label>
                        <FormikControl
                          placeholder="Tammy Simms"
                          name="name"
                          className="rounded-pill"
                        />
                      </Form.Group>

                      <Form.Group className="d-inline-block w-100 text-start mb-4">
                        <Form.Label>Email*</Form.Label>
                        <FormikControl
                          placeholder="Email"
                          name="email"
                          className="rounded-pill"
                        />
                      </Form.Group>

                      <Form.Group className="d-inline-block w-100 text-start mb-4">
                        <Form.Label>Number of students*</Form.Label>
                        <FormikControl
                          placeholder="How many students do you teach?"
                          name="numOfStudents"
                          className="rounded-pill"
                        />
                      </Form.Group>

                      <Form.Group className="d-inline-block w-100 text-start mb-4">
                        <Form.Label>State*</Form.Label>
                        <Form.Select
                            name="state"
                            className="rounded-pill"
                            placeholder="Select state"
                            onChange={(e) => formik.setFieldValue("state", e.currentTarget.value)}
                            value={formik.values.state ?? ""}
                            isInvalid={formik.touched.state && !!formik.errors.state}>
                                <option value="" disabled>-- Select state</option>
                                {statesInfo.map(state => (
                                    <option key={state} value={state}>{state}</option>
                                ))}
                        </Form.Select>
                      </Form.Group>

                      <Form.Group className="d-inline-block w-100 text-start mb-4">
                        <Form.Label>Comments</Form.Label>
                        <Form.Control 
                          as="textarea" 
                          name="comment" 
                          rows={5} 
                          placeholder="Optional Comments" 
                          onChange={(e) => formik.setFieldValue("comment", e.target.value)} />
                      </Form.Group>

                      <Button type="submit" variant="primary" className="w-25 rounded-pill mt-4 fw-bold create-account-btn" disabled={submitDisabled}>
                          <FontAwesomeIcon icon={faPaperPlane} /> Submit
                      </Button>
                      {submitted && <p className="fw-bold mt-2 fs-5">You will soon receive an email with your new account details!</p>}
                  </Form>
                </FormikProvider>
              </div>
            </div>
          </div>
        </Container>
    </FloatingPage>
  )
}

export default ScienceLandingPage
